/**
 * Payment methods API wrapper (v2 — Add-Member §E, DNKAPI-0171 **[COMING]**).
 *
 * The tenant's managed list of payment methods — the source for the Add-Member
 * payment dropdown (DNKADM-0092). Three kinds (handoff §E):
 *  - **`cash`** — always present, PROTECTED (rename allowed; removal may `400`).
 *  - **`card`** — mirrors an active payment connection (`paymentConnectionId` set);
 *    read-mostly here (its existence is driven by the connection, not this list).
 *  - **`external`** — admin-added custom labels (Venmo/PayPal).
 *
 * This module owns the full CRUD + the canonical fail-closed
 * **{@link usePaymentMethodPickList}** that DNKADM-0092 consumes. It mirrors the
 * defensive-parse idiom of `creditBuckets.ts` (numeric ids → string, a bad row
 * dropped, a non-array success treated as untrustworthy for the pick-list).
 *
 * **Forward-compatible (DNKAPI-0171 not yet merged).** An ABSENT endpoint (`404`)
 * is the designed pre-ship state, NOT an error: the pick-list degrades to a
 * **Cash-only** fallback so the Add-Member payment block always has at least Cash,
 * and the settings screen shows a neutral "not available yet" state. Genuine
 * upstream/network/malformed-body failures still fail-closed (a `PickListError`),
 * so a real outage can never masquerade as "loaded, Cash-only".
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded
 * 401→refresh→retry; the mutating-audit `X-Session-Mode` stamp fires on the writes.
 *
 * **Endpoints (handoff §E — verify vs. Swagger when DNKAPI-0171 lands):**
 *  - `GET    /v2/admin/payment-methods?status=active|all` → {@link PaymentMethod}`[]`.
 *  - `POST   /v2/admin/payment-methods` ← `{ label, kind:'external' }` → the created row (`409` dup label).
 *  - `PUT    /v2/admin/payment-methods/{id}` ← `{ label?, sortOrder?, active? }` → the updated row.
 *  - `DELETE /v2/admin/payment-methods/{id}` → `204` (Cash protected — removal may `400`).
 */
import { request } from './client';
import { useCallback } from 'react';
import { ApiError } from './errors';
import { usePickList, type PickList } from '@/hooks/usePickList';
import { PickListError } from './picklists';

/** v2 admin payment-methods base path (bearer surface). */
const PATH = '/v2/admin/payment-methods';

/** The three method kinds (handoff §E). An unknown wire kind degrades to `external`. */
export type PaymentMethodKind = 'cash' | 'card' | 'external';

/** The list filter (§E supports `active|all` only — no `inactive`). */
export type PaymentMethodStatus = 'active' | 'all';

/**
 * One payment-method row (§E). `id` is the owned v2 id everything downstream keys
 * off. `paymentConnectionId` is the originating connection for a `card` method (and
 * `null` for `cash`/`external`). `active` is the soft-delete/visibility flag;
 * `sortOrder` is the display/dropdown order.
 */
export interface PaymentMethod {
  id: string;
  label: string;
  kind: PaymentMethodKind;
  /** The originating connection for a `card` method; `null` otherwise. */
  paymentConnectionId: string | null;
  active: boolean;
  sortOrder: number;
}

/** Create body — admins add only `external` methods (cards derive from connections). */
export interface CreatePaymentMethodInput {
  label: string;
  kind: 'external';
}

/**
 * Update body — every field optional (rename, reorder, and activate/deactivate are
 * all `PUT`s with the single changed field). Absent keys are omitted from the wire
 * body (`exactOptionalPropertyTypes`).
 */
export interface UpdatePaymentMethodInput {
  label?: string;
  sortOrder?: number;
  active?: boolean;
}

/**
 * The minimal `{ id, label, kind }` shape {@link usePaymentMethodPickList} returns —
 * the canonical option the Add-Member payment dropdown (DNKADM-0092) binds.
 */
export interface PaymentMethodOption {
  id: string;
  label: string;
  kind: PaymentMethodKind;
}

/**
 * The Cash-only fallback the pick-list yields when the endpoint isn't live yet
 * (DNKAPI-0171 **[COMING]**). The synthetic `id` is a sentinel — once the real
 * endpoint ships, the server-owned Cash row (with its real id) replaces it.
 */
const CASH_ONLY_FALLBACK: PaymentMethodOption = { id: 'cash', label: 'Cash', kind: 'cash' };

// --- Defensive parsing helpers (mirrors creditBuckets.ts) ----------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 ids arrive as
 * numbers on the wire and are normalized to the `string` the UI uses — a finite
 * number → `String(n)`, a non-empty string → trimmed, else null.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** A finite number, or a fallback when absent/unparseable. */
function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Map a raw kind to the known union; an unknown/absent kind degrades to `external`. */
function asKind(value: unknown): PaymentMethodKind {
  return value === 'cash' || value === 'card' ? value : 'external';
}

/**
 * Parse one raw method, or null when it has no usable `id` OR `label` (a row that
 * can't be identified or labeled is dropped rather than surfaced half-formed). The
 * wire flag is `active` (tolerate legacy `isActive`); `sortOrder` defaults to `0`.
 */
function parseMethod(raw: unknown): PaymentMethod | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  const label = asText(raw['label']);
  if (id === null || label === null) return null;
  return {
    id,
    label,
    kind: asKind(raw['kind']),
    paymentConnectionId: asId(raw['paymentConnectionId']),
    active: asBoolean(raw['active'] ?? raw['isActive'], true),
    sortOrder: asNumber(raw['sortOrder'], 0),
  };
}

/**
 * Extract the methods array TOLERANTLY from a successful body, returning `null`
 * when NEITHER a bare array NOR a known wrapper key
 * (`paymentMethods`/`methods`/`content`) yields one. The list read turns `null`
 * into `[]` (a real first-run state); the fail-closed pick-list turns it into a
 * {@link PickListError} (a non-array success cannot be trusted as "complete").
 */
function extractMethods(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['paymentMethods'])) return body['paymentMethods'];
    if (Array.isArray(body['methods'])) return body['methods'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/** Build the JSON body for an update, omitting absent optional keys. */
function toUpdateBody(input: UpdatePaymentMethodInput): Record<string, unknown> {
  const json: Record<string, unknown> = {};
  if (input.label !== undefined) json['label'] = input.label;
  if (input.sortOrder !== undefined) json['sortOrder'] = input.sortOrder;
  if (input.active !== undefined) json['active'] = input.active;
  return json;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/payment-methods?status=…` → the tenant's payment methods,
 * defensively parsed (a bad entry dropped). `status` defaults to `active`; pass
 * `all` to include deactivated rows (the settings screen does, to manage them). A
 * body that yields no array degrades to `[]`. Throws a typed `ApiError` on a
 * non-2xx response — INCLUDING a `404` (NOT_FOUND) when the endpoint isn't live;
 * the screen handles that as the neutral forward-compat state.
 */
export async function listPaymentMethods(
  status: PaymentMethodStatus = 'active',
): Promise<PaymentMethod[]> {
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return (extractMethods(body) ?? [])
    .map(parseMethod)
    .filter((m): m is PaymentMethod => m !== null);
}

/**
 * `POST /v2/admin/payment-methods` → create an `external` method. Returns the
 * re-parsed created row. Throws a typed `ApiError` (`409` CONFLICT on a duplicate
 * label, `400` VALIDATION, upstream).
 */
export async function createPaymentMethod(
  input: CreatePaymentMethodInput,
): Promise<PaymentMethod | null> {
  const body = await request<unknown>('POST', PATH, {
    json: { label: input.label, kind: input.kind },
  });
  return parseMethod(body);
}

/**
 * `PUT /v2/admin/payment-methods/{id}` → rename (`label`), reorder (`sortOrder`),
 * or activate/deactivate (`active`). Returns the re-parsed updated row. Throws a
 * typed `ApiError` (`409` on a duplicate label, etc.).
 */
export async function updatePaymentMethod(
  id: string,
  input: UpdatePaymentMethodInput,
): Promise<PaymentMethod | null> {
  const body = await request<unknown>('PUT', `${PATH}/${encodeURIComponent(id)}`, {
    json: toUpdateBody(input),
  });
  return parseMethod(body);
}

/**
 * `DELETE /v2/admin/payment-methods/{id}` → remove a method. Cash is PROTECTED: a
 * delete attempt may `400` (the screen disables Cash's delete and surfaces a `400`
 * gracefully rather than crashing). Throws a typed `ApiError` on a non-2xx.
 */
export async function deletePaymentMethod(id: string): Promise<void> {
  await request<void>('DELETE', `${PATH}/${encodeURIComponent(id)}`);
}

// --- The shared, forward-compatible payment-method pick-list -------------------

/**
 * Load the active payment methods as `{ id, label, kind }` options. **Forward-
 * compatible + fail-closed:**
 *  - on success, maps the active methods (an empty success is a real `[]`);
 *  - on an ABSENT endpoint (`404` — DNKAPI-0171 not live), degrades to a
 *    **Cash-only** fallback so the Add-Member payment block always has Cash;
 *  - on ANY other failure (upstream/network) OR a malformed (non-array) `200`
 *    body, throws the shared {@link PickListError} so the loader renders
 *    error + Retry — a real outage never masquerades as "loaded, Cash-only".
 *
 * Mirrors `getCreditBucketOptions`, reusing `PickListError` so `usePickList`'s
 * `failClosed` detection (and the loader copy) work unchanged.
 */
export async function getPaymentMethodOptions(): Promise<PaymentMethodOption[]> {
  try {
    const body = await request<unknown>('GET', PATH, { query: { status: 'active' } });
    const list = extractMethods(body);
    // A non-array success cannot be trusted as a COMPLETE list — fail-closed.
    if (list === null) throw new Error('expected an array of payment methods');
    return list
      .map(parseMethod)
      .filter((m): m is PaymentMethod => m !== null)
      .map((m) => ({ id: m.id, label: m.label, kind: m.kind }));
  } catch (cause) {
    // Forward-compat: an absent endpoint (404) is the designed pre-0171 state —
    // degrade to Cash-only, NOT fail-closed.
    if (cause instanceof ApiError && cause.code === 'NOT_FOUND') {
      return [CASH_ONLY_FALLBACK];
    }
    throw new PickListError('payment-methods', cause);
  }
}

/**
 * `usePaymentMethodPickList()` — the canonical payment-method selector the
 * Add-Member payment dropdown (DNKADM-0092) consumes. Loads live via
 * {@link getPaymentMethodOptions} through `usePickList`: `loading → ready` with the
 * `{ id, label, kind }[]` options (Cash-only when the endpoint isn't live yet), or
 * `loading → error + Retry` on a genuine failure — never a partial/empty-on-failure
 * list. Render through `<PickListLoader>` (`@/components/PickListLoader`). Name kept
 * EXACTLY — DNKADM-0092 imports it verbatim.
 */
export function usePaymentMethodPickList(): PickList<PaymentMethodOption> {
  return usePickList(useCallback(() => getPaymentMethodOptions(), []), []);
}
